import { Button, Empty, Popconfirm, Space, Table, Tag, Typography } from "antd";
import { type ColumnsType } from "antd/es/table";

import { helpContentText } from "@fitness-app/data-models/entities/HelpContent";

import { useDeleteHelpSnippet, useHelpSnippets, type HelpSnippet } from "./useHelpCenter";

const BLOCK_LABELS: Record<string, string> = {
  p: "Akapit",
  h2: "Nagłówek",
  h3: "Podnagłówek",
  ul: "Lista",
  ol: "Lista numerowana",
  callout: "Uwaga",
  steps: "Kroki",
  img: "Obraz",
  video: "Film",
};

/**
 * Szablony bloków treści. Powstają w edytorze („Zapisz szablon” na zaznaczonym
 * bloku) i tam też się ich używa — ta zakładka służy do przeglądania i sprzątania.
 */
export const HelpSnippetsTab = () => {
  const snippetsQuery = useHelpSnippets();
  const deleteSnippet = useDeleteHelpSnippet();

  const columns: ColumnsType<HelpSnippet> = [
    {
      title: "Nazwa",
      dataIndex: "name",
      width: 260,
      render: (name: string, snippet) => (
        <Space direction="vertical" size={0}>
          <Typography.Text strong>{name}</Typography.Text>
          {snippet.description && (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {snippet.description}
            </Typography.Text>
          )}
        </Space>
      ),
    },
    {
      title: "Bloki",
      key: "blocks",
      width: 220,
      render: (_value, snippet) => (
        <Space size={4} wrap>
          {snippet.content.map((block, index) => (
            <Tag key={index}>{BLOCK_LABELS[block.type] ?? block.type}</Tag>
          ))}
        </Space>
      ),
    },
    {
      title: "Treść",
      key: "preview",
      render: (_value, snippet) => (
        <Typography.Text type="secondary" ellipsis style={{ fontSize: 12 }}>
          {helpContentText(snippet.content).slice(0, 120)}
        </Typography.Text>
      ),
    },
    {
      title: "Akcje",
      key: "actions",
      width: 110,
      render: (_value, snippet) => (
        <Popconfirm
          title="Usunąć szablon?"
          description="Materiały, w których go użyto, zostają bez zmian."
          okText="Usuń"
          cancelText="Anuluj"
          onConfirm={() => deleteSnippet.mutate(snippet.id)}
        >
          <Button size="small" danger>
            Usuń
          </Button>
        </Popconfirm>
      ),
    },
  ];

  return (
    <Space direction="vertical" size="middle" style={{ width: "100%" }}>
      <Typography.Text type="secondary">
        Szablon zapisujesz w edytorze materiału: ustaw kursor w bloku i kliknij „Zapisz szablon”. Wstawisz go potem z
        paska albo ukośnikiem.
      </Typography.Text>
      <Table<HelpSnippet>
        rowKey="id"
        loading={snippetsQuery.isLoading}
        dataSource={snippetsQuery.data ?? []}
        columns={columns}
        pagination={false}
        scroll={{ x: 860 }}
        locale={{ emptyText: <Empty description="Nie ma jeszcze żadnego szablonu." /> }}
      />
    </Space>
  );
};
